import { Link, useNavigate } from 'react-router-dom'
import Layout from '../../components/layout/layout'
import MainNav from '../../components/homepage/main-nav'
import IconBox from '../../components/common/icon-box'
import Button from '../../components/common/button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useEffect, useRef, useState } from 'react'
import { AddressDTO, JWTToken, LanguageKey, Login2FA, LoginDto } from '@ringo/shared'
import { useUser } from '../../common/hooks/auth/useUser'
import { toast } from 'react-toastify'
import { useLocalStorage } from '../../common/hooks/useLocalStorage'
import ControlledInputText from '../../components/form/controlled-input-text'
import { useMutation } from '@tanstack/react-query'
import { object, string } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useValidationRules } from '../../common/validation-rules'
import TwoFactorAuthentication from './2fa'
import { Validation } from '../../common/types'
import { useIamApi, useUserApi, useAddressApi } from '../../common/api-context'
import { ErrorResponse } from '../../common/helpers/apiHelper'
import { Captcha } from '../../components/common/captcha'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { useTranslation } from '../../useTranslation'
import I18nText from '../../components/common/i18n-text'

const Login = () => {
    const [show2Fa, setShow2Fa] = useState<boolean>(false)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const captchaRef = useRef<HCaptcha>(null)
    const localStorage = useLocalStorage()
    const { isAuthenticated, setUser } = useUser()
    const { updateUserLanguage } = useUser()
    const userApi = useUserApi()
    const addressApi = useAddressApi()
    const iamApi = useIamApi()
    const validationRules = useValidationRules()
    const [emailNotConfirmed, setEmailNotConfirmed] = useState(false)

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/')
        }
    }, [isAuthenticated, navigate])

    const validationObject: Validation<LoginDto> = {
        username: validationRules.email,
        password: string().required(),
    }

    const schema = object(validationObject)

    const { handleSubmit, control, setValue, getValues } = useForm<Login2FA>({
        resolver: yupResolver(schema as any),
        defaultValues: {
            emailCode: '',
            rememberPassword: false,
        },
    })

    const checkList = [
        { title: t('login.quickShopping') },
        { title: t('login.saveUserDataAndSettings') },
        { title: t('login.insightIntoYourOrders') },
        { title: t('login.manageNewsletterSubscription') },
    ]

    const handleLogin = async (jwtToken: JWTToken, data: LoginDto | Login2FA) => {
        localStorage.setItem('token', jwtToken.token!)

        const userPromise = userApi.getCurrentUser()
        const addressPromise = addressApi.getMyAddresses()

        const user = (await userPromise).data
        let addresses: AddressDTO[] = []

        try {
            addresses = (await addressPromise).data
        } catch (error) {
            console.log(error)
            addresses = []
        }

        if (jwtToken?.token && data) {
            setUser(user ? { ...user, addresses } : null)
            updateUserLanguage(user?.langKey?.toLowerCase() as LanguageKey)
            toast.success(t('login.youHaveSuccessfullyLoggedIn'))
            navigate('/')
        }
    }

    const loginMutation = useMutation({
        mutationFn: async (data: LoginDto) => {
            const { response } = await captchaRef.current!.execute({ async: true })
            const { data: jwtToken } = await iamApi.login({ ...data, hcaptchaPasscode: response })

            // If 2fa is enabled than show 2fa screen
            if (jwtToken?.email2FAEnabled) {
                setShow2Fa(true)
                return
            }
            await handleLogin(jwtToken, data)
        },
        onError: async (error: ErrorResponse) => {
            if (error.response.status === 455 && error.response.data.properties.errorCode === 'error.email.not.confirmed') {
                setEmailNotConfirmed(true)
            }
        },
    })

    const twoFaMutation = useMutation({
        mutationFn: async (data: Login2FA) => {
            const { response } = await captchaRef.current!.execute({ async: true })
            const { data: jwtToken } = await iamApi.login2Fa({ ...data, hcaptchaPasscode: response })

            handleLogin(jwtToken, data)
        },
    })

    const resend2FaCodeMutation = useMutation({
        mutationFn: async (data: Login2FA) => {
            await iamApi.resend2FaCode(data)
            toast.success(t('login.2faSuccessfullySent'))
        },
    })

    const resendVerificationEmailForAttemptedAccountMutation = useMutation({
        mutationFn: async () => {
            const { response } = await captchaRef.current!.execute({ async: true })
            await iamApi.resendEmailVerification({ ...getValues(), hcaptchaPasscode: response })
        },
        onSuccess: () => {
            toast.success(t('login.verificationEmailSuccessfullySent'))
            navigate('/')
        },
    })

    const onSubmit: SubmitHandler<LoginDto> = async (data) => {
        loginMutation.mutate(data)
    }

    const update2FaValue = (val: string) => {
        if (val.length === 6) {
            setValue('emailCode', val)
            twoFaMutation.mutate(getValues())
        }
    }

    const resend2FaCode = () => {
        resend2FaCodeMutation.mutate(getValues())
    }

    return (
        <Layout>
            <MainNav />
            <Captcha ref={captchaRef} />

            <div className="mt-16 mb-6 w-2/5 mx-auto max-tablet:mt-12 max-tablet:w-2/3">
                {emailNotConfirmed ? (
                    <IconBox icon="user">
                        <div className="py-10 px-2">
                            <p className="font-bold text-xl mb-3 leading-0 max-tablet:text-base text-center">{t('verifyYourEmail')}</p>

                            <div className="text-center text-base max-tablet:text-sm mb-2">{t('verifyYourEmailDescription')}</div>
                            <div className="mt-3">
                                <Button
                                    type="squareBlue"
                                    icon="envelope"
                                    text={t('login.resendVerificationEmail')}
                                    onClick={() => resendVerificationEmailForAttemptedAccountMutation.mutate()}
                                    isLoading={resendVerificationEmailForAttemptedAccountMutation.isPending}
                                />
                            </div>
                        </div>
                    </IconBox>
                ) : (
                    <>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            {!show2Fa ? (
                                <>
                                    <IconBox icon="user">
                                        <div className="py-10 px-2">
                                            <p className="font-bold text-xl text-left mb-3 leading-0 max-tablet:text-base">{t('login.iAmACustomer')}</p>
                                            <div>
                                                <ControlledInputText
                                                    className="username"
                                                    label={t('emailAddress')}
                                                    required
                                                    name="username"
                                                    control={control}
                                                    dataCy="login-email"
                                                />
                                            </div>
                                            <div className="mt-3">
                                                <ControlledInputText
                                                    className="password"
                                                    label={t('password')}
                                                    required
                                                    name="password"
                                                    type="password"
                                                    control={control}
                                                    dataCy="login-password"
                                                />
                                            </div>
                                            <div className="p-0 mt-3 flex justify-between">
                                                <Link
                                                    to="/auth/forgot-password"
                                                    className="text-base font-light text-custom_yellow mb-1 block underline hover:no-underline max-tablet:text-sm"
                                                >
                                                    {t('login.forgotPassword')}
                                                </Link>
                                                <Link
                                                    to="/auth/register"
                                                    className="text-base font-light text-custom_yellow mb-1 block underline hover:no-underline max-tablet:text-sm"
                                                >
                                                    {t('login.registration')}
                                                </Link>
                                            </div>
                                            <div className="mt-3">
                                                <Button
                                                    type="squareBlue"
                                                    className="login"
                                                    icon="right-from-bracket"
                                                    text="Login"
                                                    isLoading={loginMutation.isPending}
                                                    dataCy="login-submit"
                                                />
                                            </div>
                                        </div>
                                    </IconBox>
                                </>
                            ) : (
                                <div className="pt-8">
                                    <IconBox icon="envelope-open-text">
                                        <p className="font-bold text-xl text-left mb-1 leading-0 max-tablet:text-base pt-8">
                                            {t('myAccount.security.twoFactorAuthentication')}
                                        </p>
                                        <p className="text-base text-left pb-9 leading-0 max-tablet:text-base">
                                            <I18nText
                                                i18nKey="myAccount.enter2faFromEmail"
                                                values={{
                                                    email: getValues('username'),
                                                }}
                                                components={{
                                                    b: <b />,
                                                }}
                                            />
                                        </p>
                                        <TwoFactorAuthentication
                                            onChange={update2FaValue}
                                            onResendCode={resend2FaCode}
                                            error={twoFaMutation.isError ? (t('myAccount.security.invalid2faToken') as string) : ''}
                                        />
                                    </IconBox>
                                </div>
                            )}
                        </form>

                        {!show2Fa && (
                            <div className="flex mt-7 mb-24 max-tablet:mb-12">
                                <ul>
                                    {checkList.map((item, index) => (
                                        <li key={index} className="flex mb-2.5 leading-0">
                                            <FontAwesomeIcon icon={['fas', 'circle-check']} className="mt-px text-custom_yellow align-middle" />
                                            <p className="ml-2 text-sm text-black">{item.title}</p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    )
}

export default Login
